import React, { useState, useEffect } from 'react';
import Cookies from 'js-cookie';
import axios from 'axios';
import { toast, ToastContainer } from 'react-toastify';
import Select from 'react-select';
import { useParams, useNavigate, useLocation } from 'react-router-dom';
import Topbar from '../../layouts/Topbar';
import Sidebar from '../../layouts/Sidebar';
import 'react-toastify/dist/ReactToastify.css';
import apiConfig, { API_BASE_URL as NAMED_API_BASE_URL } from '../../../config/api';

const API_BASE_URL = typeof apiConfig === 'string' ? apiConfig : NAMED_API_BASE_URL || 'https://pms.burrayhr.com';

const HODEmployeeGoals = () => {
  const baseUrl = API_BASE_URL;
  const { id } = useParams();
  const navigate = useNavigate();
  const location = useLocation();
  const employeeId = location.state?.employeeId || id;

  const [fetchingEmployee, setFetchingEmployee] = useState(true);
  const [goals, setGoals] = useState([]);
  const [employee, setEmployee] = useState(null);
  const [departmentEmployees, setDepartmentEmployees] = useState([]);
  const [fetchingDeptEmployees, setFetchingDeptEmployees] = useState(false);
  const [expandedTexts, setExpandedTexts] = useState({});

  // View Modal State
  const [modalOpen, setModalOpen] = useState(false);
  const [selectedGoal, setSelectedGoal] = useState(null);

  // Approval / Rejection States
  const [approvalLoading, setApprovalLoading] = useState(null);
  const [rejectModalOpen, setRejectModalOpen] = useState(false);
  const [rejectReason, setRejectReason] = useState('');
  const [rejectGoal, setRejectGoal] = useState(null);
  const [rejectSubmitting, setRejectSubmitting] = useState(false);
  const [rejectError, setRejectError] = useState(null);

  const handleViewGoal = (goal) => {
    setSelectedGoal(goal);
    setModalOpen(true);
  };

  const closeModal = () => {
    setModalOpen(false);
    setSelectedGoal(null);
  };

  const toggleTextExpansion = (goalId, field) => {
    setExpandedTexts(prev => ({
      ...prev,
      [`${goalId}-${field}`]: !prev[`${goalId}-${field}`]
    }));
  };

  const renderTruncatedText = (text, goalId, field, maxLength = 55) => {
    const safeText = (text ?? '').toString();
    if (!safeText) return '';
    if (safeText.length <= maxLength) return safeText;

    const key = `${goalId || 'unknown'}-${field}`;
    const isExpanded = !!expandedTexts[key];
    const shownText = isExpanded ? safeText : `${safeText.slice(0, maxLength)}...`;

    return (
      <span>
        <span style={{ whiteSpace: 'pre-wrap' }}>{shownText}</span>
        <button
          type="button"
          onClick={() => toggleTextExpansion(goalId || 'unknown', field)}
          className="btn btn-link p-0 ms-2 text-decoration-none fs-12 fw-semibold text-primary"
        >
          {isExpanded ? 'Read less' : 'Read more'}
        </button>
      </span>
    );
  };

  const refetchEmployeeGoals = async () => {
    if (!employeeId) return;
    try {
      const token = Cookies.get('token');
      const response = await axios.get(`${baseUrl}/api/employees/${employeeId}/`, {
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json',
        },
      });
      const emp = response.data?.employee || response.data || {};
      setEmployee(emp);
      setGoals(emp.goals || []);
    } catch (err) {
      console.error('Error refetching employee data:', err);
    }
  };

  const handleApproveGoal = async (goal) => {
    setApprovalLoading(goal.id);
    try {
      const token = Cookies.get('token');
      await axios.post(`${baseUrl}/api/goals/${goal.id}/approval/`, { action: 'approve' }, {
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json',
        },
      });
      toast.success('Goal approved!');
      await refetchEmployeeGoals();
    } catch (err) {
      console.error('Error approving goal:', err);
      toast.error(err.response?.data?.detail || err.message || 'Failed to approve goal.');
    } finally {
      setApprovalLoading(null);
    }
  };

  const openRejectModal = (goal) => {
    setRejectGoal(goal);
    setRejectReason('');
    setRejectError(null);
    setRejectModalOpen(true);
  };

  const closeRejectModal = () => {
    setRejectModalOpen(false);
    setRejectGoal(null);
    setRejectReason('');
    setRejectError(null);
  };

  const handleRejectSubmit = async (e) => {
    e.preventDefault();
    if (!rejectReason.trim()) {
      setRejectError('Please provide a reason for rejection.');
      return;
    }
    setRejectSubmitting(true);
    setRejectError(null);
    try {
      const token = Cookies.get('token');
      await axios.post(`${baseUrl}/api/goals/${rejectGoal.id}/approval/`, { action: 'reject', reason: rejectReason }, {
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json',
        },
      });
      toast.success('Goal rejected!');
      await refetchEmployeeGoals();
      closeRejectModal();
    } catch (err) {
      console.error('Error rejecting goal:', err);
      setRejectError(err.response?.data?.detail || err.message || 'Failed to reject goal.');
    } finally {
      setRejectSubmitting(false);
    }
  };

  useEffect(() => {
    const fetchEmployee = async () => {
      setFetchingEmployee(true);
      try {
        const token = Cookies.get('token');
        const response = await axios.get(`${baseUrl}/api/employees/${employeeId}/`, {
          headers: {
            'Authorization': `Bearer ${token}`,
            'Content-Type': 'application/json'
          }
        });
        const emp = response.data?.employee || response.data || {};
        setEmployee(emp);
        setGoals(emp.goals || []);
      } catch (err) {
        console.error('Error fetching employee:', err);
        toast.error('Failed to fetch employee data.');
      } finally {
        setFetchingEmployee(false);
      }
    };
    if (employeeId) fetchEmployee();
  }, [employeeId, baseUrl]);

  useEffect(() => {
    const fetchDepartmentEmployees = async () => {
      setFetchingDeptEmployees(true);
      try {
        const token = Cookies.get('token');
        const response = await axios.get(`${baseUrl}/api/department-employees`, {
          headers: {
            'Authorization': `Bearer ${token}`,
            'Content-Type': 'application/json'
          }
        });
        setDepartmentEmployees(Array.isArray(response.data) ? response.data : []);
      } catch (err) {
        console.error('Failed to fetch department employees:', err);
      } finally {
        setFetchingDeptEmployees(false);
      }
    };
    fetchDepartmentEmployees();
  }, [baseUrl]);

  const employeeOptions = departmentEmployees.map(emp => ({
    value: emp.id,
    label: `${emp.first_name || ''} ${emp.last_name || ''}`.trim() || emp.name || 'Employee'
  }));

  const handleEmployeeChange = (selectedOption) => {
    if (selectedOption) {
      navigate(`/hod/employee/${selectedOption.value}/goals`, {
        state: { employeeId: selectedOption.value }
      });
    }
  };

  const currentEmployeeId = employeeId != null ? Number(employeeId) : null;

  const renderApprovalBadge = (status) => {
    switch (status?.toLowerCase()) {
      case 'approved':
        return (
          <span className="badge bg-success-transparent text-success d-inline-flex align-items-center gap-1">
            <i className="ti ti-circle-check-filled"></i> Approved
          </span>
        );
      case 'rejected':
        return (
          <span className="badge bg-danger-transparent text-danger d-inline-flex align-items-center gap-1">
            <i className="ti ti-circle-x-filled"></i> Rejected
          </span>
        );
      case 'pending':
      default:
        return (
          <span className="badge bg-warning-transparent text-warning d-inline-flex align-items-center gap-1">
            <i className="ti ti-clock"></i> Pending
          </span>
        );
    }
  };

  return (
    <div data-sidebar-skin="dark" data-header-skin="light" data-navbar-brand-skin="dark" data-sidebar-state="expand">
      <ToastContainer position="top-right" autoClose={3000} />
      <Topbar />
      <Sidebar />
      <div className="page-wrapper">
        <div className="content pb-0">
          {/* Page Header */}
          <div className="d-md-flex d-block align-items-center justify-content-between page-breadcrumb mb-3">
            <div className="my-auto mb-2 mb-md-0 d-flex align-items-center gap-3 flex-wrap">
              <div>
                <h4 className="mb-0">Employee Goals</h4>
                <p className="text-muted mb-0 fs-13">View and approve employee KPIs</p>
              </div>

              {/* Department Employee Switcher */}
              <div style={{ minWidth: '240px' }}>
                <Select
                  options={employeeOptions}
                  value={employeeOptions.find(opt => opt.value === currentEmployeeId)}
                  onChange={handleEmployeeChange}
                  placeholder="Select Employee..."
                  isLoading={fetchingDeptEmployees}
                  classNamePrefix="react-select"
                  menuPortalTarget={typeof document !== 'undefined' ? document.body : undefined}
                />
              </div>
            </div>

            <div className="d-flex align-items-center gap-2">
              <button
                className="btn btn-primary btn-sm d-inline-flex align-items-center gap-1"
                onClick={() => navigate('/hod/employees')}
              >
                <i className="ti ti-arrow-left fs-16"></i>
                <span>Back to Employees</span>
              </button>
            </div>
          </div>

          {/* Table Container */}
          <div className="card shadow-sm border-0 mb-4">
            <div className="card-body p-0">
              {fetchingEmployee ? (
                <div className="text-center py-5">
                  <div className="spinner-border text-primary" role="status">
                    <span className="visually-hidden">Loading...</span>
                  </div>
                  <p className="text-muted mt-2">Loading employee goals...</p>
                </div>
              ) : employee ? (
                <div className="table-responsive">
                  <table className="table table-hover align-middle mb-0">
                    <thead className="table-light">
                      <tr>
                        <th>Activity</th>
                        <th>Stakeholder</th>
                        <th className="text-center">KPI Metric</th>
                        <th className="text-center">RAG Indicator</th>
                        <th className="text-center">Quarter</th>
                        <th className="text-center">Approval Status</th>
                        <th className="text-center">Actions</th>
                      </tr>
                    </thead>
                    <tbody>
                      {goals.length === 0 ? (
                        <tr>
                          <td colSpan={7} className="text-center py-4 text-muted">
                            No goals found for this employee.
                          </td>
                        </tr>
                      ) : (
                        (() => {
                          const groupedByKra = goals.reduce((acc, goal) => {
                            const kraName =
                              (goal.key_result_area && typeof goal.key_result_area === 'object'
                                ? goal.key_result_area.name
                                : goal.key_result_area || goal.name) || 'Uncategorized';
                            if (!acc[kraName]) acc[kraName] = [];
                            acc[kraName].push(goal);
                            return acc;
                          }, {});

                          return Object.entries(groupedByKra).map(([kraName, kraGoals]) => (
                            <React.Fragment key={kraName}>
                              <tr className="table-secondary">
                                <td colSpan={7} className="fw-bold text-dark py-2">
                                  <i className="ti ti-folder me-2 text-primary"></i>
                                  {kraName}{' '}
                                  <span className="fw-normal text-muted fs-13 ms-1">
                                    (KRA Weight:{' '}
                                    {kraGoals?.[0]?.key_result_area?.percentage ??
                                    kraGoals?.[0]?.kra?.percentage ??
                                    kraGoals?.[0]?.percentage ??
                                    '-'}
                                    %)
                                  </span>
                                </td>
                              </tr>
                              {kraGoals.map((goal, idx) => (
                                <tr key={goal.id || `${kraName}-${idx}`}>
                                  <td>
                                    {renderTruncatedText(goal.activity, goal.id || `${kraName}-${idx}`, 'activity')}
                                  </td>
                                  <td>{goal.stakeholder || '-'}</td>
                                  <td className="text-center fw-semibold">{goal.metric !== undefined ? `${goal.metric}%` : '-'}</td>
                                  <td className="text-center">
                                    <div
                                      className="d-inline-flex align-items-center px-2 py-1 rounded text-white fs-12 fw-semibold"
                                      style={{ backgroundColor: goal.progress_indicator || '#6c757d' }}
                                    >
                                      {goal.rag_color || 'Status'}
                                    </div>
                                  </td>
                                  <td className="text-center fw-medium">{goal.quarter || '-'}</td>
                                  <td className="text-center">{renderApprovalBadge(goal.approval_status)}</td>
                                  <td className="text-center">
                                    <div className="d-flex align-items-center justify-content-center gap-1">
                                      <button
                                        className="btn btn-sm btn-icon btn-light"
                                        title="View Details"
                                        onClick={() => handleViewGoal(goal)}
                                      >
                                        <i className="ti ti-eye fs-16 text-muted"></i>
                                      </button>

                                      {goal.approval_status === 'pending' && (
                                        <>
                                          <button
                                            className="btn btn-sm btn-icon btn-success-transparent text-success"
                                            title="Approve"
                                            onClick={() => handleApproveGoal(goal)}
                                            disabled={approvalLoading === goal.id}
                                          >
                                            {approvalLoading === goal.id ? (
                                              <span className="spinner-border spinner-border-sm"></span>
                                            ) : (
                                              <i className="ti ti-check fs-16"></i>
                                            )}
                                          </button>

                                          <button
                                            className="btn btn-sm btn-icon btn-danger-transparent text-danger"
                                            title="Reject"
                                            onClick={() => openRejectModal(goal)}
                                          >
                                            <i className="ti ti-x fs-16"></i>
                                          </button>
                                        </>
                                      )}
                                    </div>
                                  </td>
                                </tr>
                              ))}
                            </React.Fragment>
                          ));
                        })()
                      )}
                    </tbody>
                  </table>
                </div>
              ) : (
                <div className="alert alert-danger m-3">Employee not found.</div>
              )}
            </div>
          </div>
        </div>
      </div>

      {/* View Goal Details Modal */}
      {modalOpen && selectedGoal && (
        <div className="modal fade show d-block" tabIndex="-1" style={{ backgroundColor: 'rgba(0,0,0,0.5)' }}>
          <div className="modal-dialog modal-dialog-centered modal-lg">
            <div className="modal-content border-0 shadow">
              <div className="modal-header">
                <h5 className="modal-title fw-bold">KPI Details</h5>
                <button type="button" className="btn-close" onClick={closeModal}></button>
              </div>
              <div className="modal-body">
                <div className="table-responsive border rounded">
                  <table className="table table-bordered mb-0">
                    <tbody>
                      <tr>
                        <th className="bg-light w-35 text-muted">Key Result Area</th>
                        <td className="fw-medium">{selectedGoal.key_result_area?.name || selectedGoal.name || 'N/A'}</td>
                      </tr>
                      <tr>
                        <th className="bg-light text-muted">KRA Weight</th>
                        <td className="fw-medium">{selectedGoal.key_result_area?.percentage || selectedGoal.percentage || '0'}%</td>
                      </tr>
                      <tr>
                        <th className="bg-light text-muted">Activity / KPI</th>
                        <td>{selectedGoal.activity}</td>
                      </tr>
                      <tr>
                        <th className="bg-light text-muted">KPI Metric</th>
                        <td>{selectedGoal.metric}%</td>
                      </tr>
                      <tr>
                        <th className="bg-light text-muted">Stakeholder</th>
                        <td>{selectedGoal.stakeholder || '-'}</td>
                      </tr>
                      <tr>
                        <th className="bg-light text-muted">Quarter / Period</th>
                        <td>{selectedGoal.quarter || '-'}</td>
                      </tr>
                      <tr>
                        <th className="bg-light text-muted">RAG Indicator</th>
                        <td>
                          <span
                            className="badge text-white py-1 px-2"
                            style={{ backgroundColor: selectedGoal.progress_indicator || '#6c757d' }}
                          >
                            {selectedGoal.rag_color || 'Status'}
                          </span>
                        </td>
                      </tr>
                      <tr>
                        <th className="bg-light text-muted">Completion Status</th>
                        <td>
                          {selectedGoal.status
                            ? selectedGoal.status.charAt(0).toUpperCase() + selectedGoal.status.slice(1).replace(/_/g, ' ')
                            : 'N/A'}
                        </td>
                      </tr>
                      <tr>
                        <th className="bg-light text-muted">Is Overdue</th>
                        <td>
                          <span className={`badge ${selectedGoal.is_overdue ? 'bg-danger-transparent text-danger' : 'bg-success-transparent text-success'}`}>
                            {selectedGoal.is_overdue ? 'Yes' : 'No'}
                          </span>
                        </td>
                      </tr>
                      <tr>
                        <th className="bg-light text-muted">Approval Status</th>
                        <td>{renderApprovalBadge(selectedGoal.approval_status)}</td>
                      </tr>
                      <tr>
                        <th className="bg-light text-muted">Due Date</th>
                        <td>
                          {selectedGoal.completion_date
                            ? new Date(selectedGoal.completion_date).toLocaleDateString('en-GB')
                            : 'N/A'}
                        </td>
                      </tr>
                      {selectedGoal.approval_status === 'rejected' && selectedGoal.rejected_reason && (
                        <tr>
                          <th className="bg-light text-muted">Rejection Reason</th>
                          <td className="text-danger fw-medium">{selectedGoal.rejected_reason}</td>
                        </tr>
                      )}
                    </tbody>
                  </table>
                </div>
              </div>
              <div className="modal-footer bg-light">
                <button type="button" className="btn btn-secondary" onClick={closeModal}>
                  Close
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Reject Goal Modal */}
      {rejectModalOpen && rejectGoal && (
        <div className="modal fade show d-block" tabIndex="-1" style={{ backgroundColor: 'rgba(0,0,0,0.5)', zIndex: 1060 }}>
          <div className="modal-dialog modal-dialog-centered">
            <div className="modal-content border-0 shadow">
              <div className="modal-header">
                <h5 className="modal-title fw-bold">Reject Goal</h5>
                <button type="button" className="btn-close" onClick={closeRejectModal}></button>
              </div>
              <form onSubmit={handleRejectSubmit}>
                <div className="modal-body">
                  <div className="mb-3">
                    <label htmlFor="rejection-reason" className="form-label fw-medium">
                      Rejection Reason <span className="text-danger">*</span>
                    </label>
                    <textarea
                      id="rejection-reason"
                      name="rejection_reason"
                      value={rejectReason}
                      onChange={(e) => setRejectReason(e.target.value)}
                      required
                      className="form-control"
                      placeholder="Enter reason for rejection..."
                      rows={4}
                      disabled={rejectSubmitting}
                    />
                  </div>
                  {rejectError && (
                    <div className="alert alert-danger py-2 fs-13 mb-0">{rejectError}</div>
                  )}
                </div>
                <div className="modal-footer bg-light">
                  <button
                    type="button"
                    className="btn btn-light"
                    onClick={closeRejectModal}
                    disabled={rejectSubmitting}
                  >
                    Cancel
                  </button>
                  <button
                    type="submit"
                    className="btn btn-danger d-flex align-items-center gap-2"
                    disabled={rejectSubmitting || !rejectReason.trim()}
                  >
                    {rejectSubmitting && <span className="spinner-border spinner-border-sm"></span>}
                    <span>Reject Goal</span>
                  </button>
                </div>
              </form>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default HODEmployeeGoals;